// Aus Settings ein vollständiges CSS für Paged.js bauen.
// CSS Paged Media: @page, @top-left, @bottom-center, target-counter() etc.
/** Löst eine Farb-Referenz auf: "@name" → Palette-Lookup, sonst direkter Hex. */
export function resolveColor(ref, s) {
    if (!ref)
        return s.colors.text;
    if (!ref.startsWith("@"))
        return ref;
    const name = ref.slice(1);
    if (name === "text")
        return s.colors.text;
    if (name === "accent")
        return s.colors.accent;
    if (name === "muted")
        return s.colors.muted;
    return s.colors.custom?.[name] ?? s.colors.text;
}
const FONT_ASSET_PATH = "/assets/fonts/";
/** Eingebetteter Symbol-Fallback (Pfeile, Haken, Bullets, Sterne …). */
const FALLBACK_FAMILY = "BookFallback";
const FALLBACK_FACES = [
    { weight: 400, italic: false, file: "DejaVuSans.ttf" },
    { weight: 700, italic: false, file: "DejaVuSans-Bold.ttf" },
];
/** Familien, die wir als statische Dateien mitliefern (kein Type-3-Risiko). */
const SELF_HOSTED_FONTS = {
    Lora: [
        { weight: 400, italic: false, file: "Lora-Regular.ttf" },
        { weight: 400, italic: true, file: "Lora-Italic.ttf" },
        { weight: 600, italic: false, file: "Lora-SemiBold.ttf" },
        { weight: 700, italic: false, file: "Lora-Bold.ttf" },
    ],
    "Playfair Display": [
        { weight: 400, italic: false, file: "PlayfairDisplay-Regular.ttf" },
        { weight: 600, italic: false, file: "PlayfairDisplay-SemiBold.ttf" },
        { weight: 700, italic: false, file: "PlayfairDisplay-Bold.ttf" },
    ],
    "EB Garamond": [
        { weight: 400, italic: false, file: "EBGaramond-Regular.ttf" },
        { weight: 400, italic: true, file: "EBGaramond-Italic.ttf" },
        { weight: 500, italic: false, file: "EBGaramond-Medium.ttf" },
        { weight: 600, italic: false, file: "EBGaramond-SemiBold.ttf" },
    ],
};
/** Registry case-/whitespace-tolerant nachschlagen (CSS-Familiennamen sind
 *  case-insensitiv). Liefert [kanonischer Name, Faces] oder null. */
function lookupHosted(family) {
    const norm = (x) => x.trim().toLowerCase();
    const want = norm(family);
    for (const key of Object.keys(SELF_HOSTED_FONTS)) {
        if (norm(key) === want)
            return [key, SELF_HOSTED_FONTS[key]];
    }
    return null;
}
function faceRule(family, f) {
    return (`@font-face{font-family:"${family}";` +
        `font-style:${f.italic ? "italic" : "normal"};font-weight:${f.weight};` +
        `font-display:swap;src:url("${FONT_ASSET_PATH}${f.file}") format("truetype");}`);
}
/** Alle im Dokument verwendeten Font-Familien einsammeln. */
function usedFamilies(s) {
    return [
        s.typography.bodyFontFamily,
        s.headings.headingFontFamily,
        s.headings.h1.fontFamily,
        s.headings.h2.fontFamily,
        s.headings.h3.fontFamily,
        s.headings.h4.fontFamily,
        s.dropCap.fontFamily,
    ].filter(Boolean);
}
/** @font-face-Regeln: selbst-gehostete Familien (sofern verwendet) + Fallback. */
function buildFontFaces(s) {
    const rules = [];
    const seen = new Set();
    for (const fam of usedFamilies(s)) {
        const hit = lookupHosted(fam);
        if (!hit || seen.has(hit[0]))
            continue;
        seen.add(hit[0]);
        rules.push(...hit[1].map((f) => faceRule(hit[0], f)));
    }
    // Symbol-Fallback IMMER laden, damit „→" & Co. eingebettet statt System-Font.
    rules.push(...FALLBACK_FACES.map((f) => faceRule(FALLBACK_FAMILY, f)));
    return rules.join("\n");
}
/** @import NUR für nicht selbst-gehostete Familien (eigene Google-URLs). */
function buildFontImports(s) {
    const imports = [];
    const bodyHosted = !!lookupHosted(s.typography.bodyFontFamily);
    const headHosted = !!lookupHosted(s.headings.headingFontFamily);
    if (s.typography.bodyFontUrl && !bodyHosted) {
        imports.push(`@import url("${s.typography.bodyFontUrl}");`);
    }
    if (s.headings.headingFontUrl &&
        !headHosted &&
        s.headings.headingFontUrl !== s.typography.bodyFontUrl) {
        imports.push(`@import url("${s.headings.headingFontUrl}");`);
    }
    return imports.join("\n");
}
export function buildStylesheet(s) {
    const W = s.page.widthMm;
    const H = s.page.heightMm;
    const bleed = s.page.bleedMm;
    // Randabfallendes Cover: Bild wird um bleed mm pro Seite vergrößert und
    // negativ versetzt, damit es bis in den Anschnitt reicht. Nur bei Bild-Cover
    // und gesetztem Beschnitt sinnvoll.
    const coverBleed = !!s.cover.bleedExtend && bleed > 0 && s.cover.mode === "image";
    // Gilt für die Umschlag-Wrapper (Innenseiten + Rückseite): Bild randabfallend.
    const wrapBleed = !!s.cover.bleedExtend && bleed > 0;
    // Liegt vor der Titelseite schon eine Cover-/Umschlag-Innenseite? Dann soll
    // die Front-Matter (Titel → Impressum → ToC) lückenlos dahinter fließen statt
    // den Buchblock-Anfang per Recto-Umbruch zu erzwingen (sonst Vakat-Leerseite
    // zwischen Cover und Titel). Ergebnis z. B.: Cover S.1, Titel S.2,
    // Impressum S.3, Inhaltsverzeichnis S.4.
    const coverPageRendered = s.cover.includeInPdf &&
        ((s.cover.mode === "image" && !!s.cover.imageDataUrl) || s.cover.mode === "template");
    const frontPagesBeforeTitle = coverPageRendered || !!s.cover.insideFront?.include;
    // CSS für eine vollflächige Umschlag-Wrapper-Seite (Innenseite/Rückseite),
    // analog zu .cover-page. `brk` legt den Seitenumbruch fest: "after" trennt zur
    // Folgeseite (vordere Seiten), "before" beginnt eine neue Seite (hintere Seiten).
    const wrapperPageCss = (cls, pageName, brk) => `
.${cls} {
  page: ${pageName};
  position: relative;
  width: ${W}mm;
  height: ${H}mm;
  margin: 0 !important;
  padding: 0 !important;
  line-height: 0;
  overflow: ${wrapBleed ? "visible" : "hidden"};
  break-${brk}: page;
}
@page ${pageName} {
  margin: 0 !important;
  margin-top: 0 !important;
  margin-right: 0 !important;
  margin-bottom: 0 !important;
  margin-left: 0 !important;
  @top-left   { content: none; } @top-center   { content: none; } @top-right   { content: none; }
  @bottom-left{ content: none; } @bottom-center{ content: none; } @bottom-right{ content: none; }
}
.${cls} img {
  position: absolute;
  ${wrapBleed
        ? `top: -${bleed}mm;
  left: -${bleed}mm;
  width: ${W + 2 * bleed}mm;
  height: ${H + 2 * bleed}mm;`
        : `inset: 0;
  width: ${W}mm;
  height: ${H}mm;`}
  object-fit: cover;
  display: block;
  margin: 0;
  padding: 0;
  border: 0;
}
${wrapBleed ? `.pagedjs_page.pagedjs_${pageName}_page .pagedjs_page_content,
.pagedjs_page.pagedjs_${pageName}_page .pagedjs_area { overflow: visible !important; }
` : ""}`;
    // Running-Header-Variablen werden über benannte CSS-Strings befüllt:
    //   book-title/author → unsichtbare Anker-Divs am Body-Anfang (siehe
    //     buildBookDocument); Paged.js ignoriert string-set auf <body> selbst.
    //   chapter-title/section-title → string-set auf h1/h2 (weiter unten).
    const headingCss = (sel, h) => `
${sel} {
  font-family: ${cssFont(h.fontFamily)};
  font-size: ${h.sizePt}pt;
  font-weight: ${h.weight};
  line-height: ${h.lineHeight};
  color: ${resolveColor(h.color, s)};
  ${h.italic ? "font-style: italic;" : ""}
  ${h.uppercase ? "text-transform: uppercase;" : ""}
  text-align: ${h.align};
  margin-top: ${h.marginTopEm}em;
  margin-bottom: ${h.marginBottomEm}em;
  letter-spacing: ${h.letterSpacingEm}em;
  break-after: avoid;
  ${h.pageBreakBefore !== "none"
        ? `break-before: ${
        // ohne Spread-Layout machen "right"/"left"-Breaks keinen Sinn:
        // sie erzeugen leere Vakat-Verso-Seiten. Auf "page" downgraden.
        !s.page.spreadLayout
            ? "page"
            : h.pageBreakBefore === "right"
                ? "right"
                : h.pageBreakBefore === "left"
                    ? "left"
                    : "page"};`
        : ""}
}`;
    // Margin-Box-Content für Header/Footer
    const mkBox = (val) => {
        if (!val)
            return "none";
        // Variablen ersetzen. Die benannten Strings werden gesetzt von:
        //   book-title/author   → unsichtbare Anker-Divs am Anfang des <body>
        //   chapter-title       → string-set auf jedem <h1>
        //   section-title       → string-set auf jedem <h2>
        // (Paged.js ignoriert string-set auf <body> selbst — deshalb die Anker.)
        const replaced = val
            .replace(/\{book_title\}/g, `" " string(book-title) " "`)
            .replace(/\{author\}/g, `" " string(author) " "`)
            .replace(/\{chapter_title\}/g, `" " string(chapter-title) " "`)
            .replace(/\{section_title\}/g, `" " string(section-title) " "`)
            .replace(/\{page_number\}/g, `counter(page)`)
            .replace(/\{page_total\}/g, `counter(pages)`);
        // Strings konkatenieren: alles was als "text" markiert ist, plus counter()-Aufrufe
        // Vereinfachung: wenn keine Variable enthalten ist, plain string ausgeben
        if (!/string\(|counter\(/.test(replaced))
            return `"${val.replace(/"/g, '\\"')}"`;
        return replaced;
    };
    const headerEnabled = s.runningHeader.enabled;
    const footerEnabled = s.runningFooter.enabled;
    return `
/* ==========================================================================
   md2bookpdf – Stylesheet, generiert aus Settings (Version ${s.version})
   ========================================================================== */

${buildFontImports(s)}
${buildFontFaces(s)}

/* ---------- @page (Größe, Ränder, Header/Footer) ----------------------- */

@page {
  size: ${W}mm ${H}mm;
  margin-top: ${s.page.marginTopMm}mm;
  margin-bottom: ${s.page.marginBottomMm}mm;
  margin-left: ${s.page.spreadLayout ? s.page.marginOuterMm : s.page.marginInnerMm}mm;
  margin-right: ${s.page.spreadLayout ? s.page.marginOuterMm : s.page.marginOuterMm}mm;
  ${bleed > 0 ? `bleed: ${bleed}mm;` : ""}
  ${s.page.cropMarks ? "marks: crop;" : ""}

  ${headerEnabled
        ? `
  @top-left  { content: ${mkBox(s.runningHeader.versoLeft)}; font-size: 9pt; color: ${s.colors.muted}; vertical-align: bottom; }
  @top-right { content: ${mkBox(s.runningHeader.versoRight)}; font-size: 9pt; color: ${s.colors.muted}; vertical-align: bottom; }
  `
        : ""}

  ${footerEnabled
        ? `
  @bottom-left   { content: ${mkBox(s.runningFooter.versoLeft)};   font-size: 9pt; color: ${s.colors.muted}; vertical-align: top; }
  @bottom-center { content: ${mkBox(s.runningFooter.versoCenter)}; font-size: 9pt; color: ${s.colors.muted}; vertical-align: top; }
  @bottom-right  { content: ${mkBox(s.runningFooter.versoRight)};  font-size: 9pt; color: ${s.colors.muted}; vertical-align: top; }
  `
        : ""}
}

${s.page.spreadLayout
        ? `
@page :left {
  margin-left:  ${s.page.marginOuterMm}mm;
  margin-right: ${s.page.marginInnerMm}mm;
  ${headerEnabled
            ? `
  @top-left  { content: ${mkBox(s.runningHeader.versoLeft)};  font-size: 9pt; color: ${s.colors.muted}; vertical-align: bottom; }
  @top-right { content: ${mkBox(s.runningHeader.versoRight)}; font-size: 9pt; color: ${s.colors.muted}; vertical-align: bottom; }
  `
            : ""}
  ${footerEnabled
            ? `
  @bottom-left   { content: ${mkBox(s.runningFooter.versoLeft)};   font-size: 9pt; color: ${s.colors.muted}; vertical-align: top; }
  @bottom-center { content: ${mkBox(s.runningFooter.versoCenter)}; font-size: 9pt; color: ${s.colors.muted}; vertical-align: top; }
  @bottom-right  { content: ${mkBox(s.runningFooter.versoRight)};  font-size: 9pt; color: ${s.colors.muted}; vertical-align: top; }
  `
            : ""}
}
@page :right {
  margin-left:  ${s.page.marginInnerMm}mm;
  margin-right: ${s.page.marginOuterMm}mm;
  ${headerEnabled
            ? `
  @top-left  { content: ${mkBox(s.runningHeader.rectoLeft)};  font-size: 9pt; color: ${s.colors.muted}; vertical-align: bottom; }
  @top-right { content: ${mkBox(s.runningHeader.rectoRight)}; font-size: 9pt; color: ${s.colors.muted}; vertical-align: bottom; }
  `
            : ""}
  ${footerEnabled
            ? `
  @bottom-left   { content: ${mkBox(s.runningFooter.rectoLeft)};   font-size: 9pt; color: ${s.colors.muted}; vertical-align: top; }
  @bottom-center { content: ${mkBox(s.runningFooter.rectoCenter)}; font-size: 9pt; color: ${s.colors.muted}; vertical-align: top; }
  @bottom-right  { content: ${mkBox(s.runningFooter.rectoRight)};  font-size: 9pt; color: ${s.colors.muted}; vertical-align: top; }
  `
            : ""}
}
`
        : ""}

/* Kapitel-Opener läuft jetzt rein über break-before:right auf h1 (siehe headingCss).
   Die benannte @page chapter-opener-Regel hat Paged.js gecrashed und ist entfernt. */

/* ---------- Body-Typografie ------------------------------------------- */

html, body {
  font-family: ${cssFont(s.typography.bodyFontFamily)};
  font-size: ${s.typography.bodySizePt}pt;
  line-height: ${s.typography.bodyLineHeight};
  color: ${s.colors.text};
  margin: 0;
  padding: 0;
  -webkit-font-smoothing: antialiased;
}

p {
  margin: 0;
  text-align: ${s.typography.bodyAlignment};
  ${s.typography.hyphenation ? "hyphens: auto;" : "hyphens: none;"}
  text-indent: ${s.typography.firstLineIndentEm}em;
  ${s.typography.paragraphSpacingEm ? `margin-bottom: ${s.typography.paragraphSpacingEm}em;` : ""}
  widows: ${s.widows};
  orphans: ${s.orphans};
}

html { -webkit-hyphens: auto; hyphens: auto; }
body { hyphenate-limit-chars: 7 3 3; }

/* Erster Absatz nach H1/H2/H3 ohne Einzug (klassische Buchsatzregel) */
h1 + p, h2 + p, h3 + p, h4 + p,
.epigraph + p, .pull-quote + p {
  text-indent: 0;
}

strong { font-weight: 600; }
em { font-style: italic; }

a { color: inherit; text-decoration: none; }

/* String-Set für Header-Variablen: unsichtbare Anker am Body-Anfang, die den
   Buchtitel/Autor als benannte CSS-Strings setzen. Sie MÜSSEN gerendert werden
   (kein display:none — Paged.js überspränge die Knoten sonst), nehmen aber
   keinen Platz ein und sind unsichtbar. string-set auf <body> funktioniert in
   Paged.js NICHT, daher dieser Umweg. */
.book-title-anchor, .author-anchor {
  height: 0;
  line-height: 0;
  font-size: 0;
  overflow: hidden;
  visibility: hidden;
  margin: 0;
  padding: 0;
}
.book-title-anchor { string-set: book-title content(); }
.author-anchor { string-set: author content(); }

${headingCss("h1", s.headings.h1)}
${headingCss("h2", s.headings.h2)}
${headingCss("h3", s.headings.h3)}
${headingCss("h4", s.headings.h4)}

/* ---------- PDF-Lesezeichen / Document-Outline -----------------------
   CSS Generated Paged Media Level 3: bookmark-level + bookmark-label
   erzeugen Outline-Einträge im PDF. Wird von Weasyprint/Prince/Antenna
   House nativ unterstützt. Chromium ignoriert die Direktiven, generiert
   aber seit ca. Version 115 automatisch eine Outline aus den HTML-
   Headings im gedruckten DOM — also liefern wir beides parallel. */
h1 {
  bookmark-level: 1;
  bookmark-label: content(text);
  bookmark-state: open;
  string-set: chapter-title content(text);
}
h2 {
  bookmark-level: 2;
  bookmark-label: content(text);
  bookmark-state: open;
  string-set: section-title content(text);
}
h3 {
  bookmark-level: 3;
  bookmark-label: content(text);
  bookmark-state: closed;
}
h4 {
  bookmark-level: 4;
  bookmark-label: content(text);
  bookmark-state: closed;
}
/* Cover-Image und ToC-Title sollen NICHT in der Outline auftauchen. */
.cover-page h1, .cover-page h2,
.toc .toc-title { bookmark-level: none; }

/* ---------- Cover / Titelseite ---------------------------------------- */

.cover-page {
  page: cover;
  position: relative;
  /* Explizite Pixel-Maße statt 100% — sonst kollabiert die Section in
     Paged.js' Flow-Layout, weil das Parent-Height nicht festgelegt ist. */
  width: ${W}mm;
  height: ${H}mm;
  margin: 0 !important;
  padding: 0 !important;
  line-height: 0;  /* sonst Baseline-Gap unter inline-img */
  overflow: ${coverBleed ? "visible" : "hidden"};
  break-after: page;  /* sauberer Page-Break vor Title-Page */
}
@page cover {
  margin: 0 !important;
  margin-top: 0 !important;
  margin-right: 0 !important;
  margin-bottom: 0 !important;
  margin-left: 0 !important;
  @top-left   { content: none; } @top-center   { content: none; } @top-right   { content: none; }
  @bottom-left{ content: none; } @bottom-center{ content: none; } @bottom-right{ content: none; }
}
.cover-page img {
  position: absolute;
  ${coverBleed
        ? `top: -${bleed}mm;
  left: -${bleed}mm;
  width: ${W + 2 * bleed}mm;
  height: ${H + 2 * bleed}mm;`
        : `inset: 0;
  width: ${W}mm;
  height: ${H}mm;`}
  object-fit: cover;
  display: block;
  margin: 0;
  padding: 0;
  border: 0;
}
${coverBleed ? `/* Paged.js clippt den Seiteninhalt per overflow:hidden — für die
   randabfallende Coverseite aufheben, damit das Bild in den Anschnitt reicht. */
.pagedjs_page.pagedjs_cover_page .pagedjs_page_content,
.pagedjs_page.pagedjs_cover_page .pagedjs_area { overflow: visible !important; }
` : ""}

/* ---------- Umschlag-Innenseiten + Rückseite (U2/U3/U4) --------------- */
${wrapperPageCss("inside-front-page", "insidefront", "after")}
${wrapperPageCss("inside-back-page", "insideback", "before")}
${wrapperPageCss("back-cover-page", "backcover", "before")}

.title-page {
  page: titlepage;
  /* Ohne vorangehende Cover-/Umschlagseite: break-before steuert, ob der
     Buchblock recto/verso beginnt (ggf. mit Vakat-Leerseite). Mit Cover davor:
     einfach „page" → Titel folgt lückenlos auf das Cover, keine Leerseite. */
  break-before: ${frontPagesBeforeTitle ? "page" : s.page.contentStartSide === "left" ? "left" : "right"};
  text-align: center;
  padding-top: 30%;
  margin: 0;
}
@page titlepage {
  @top-left { content: none; } @top-right { content: none; }
  @bottom-center { content: none; }
}
.title-page .book-title { font-family: ${cssFont(s.headings.h1.fontFamily)}; font-size: ${s.headings.h1.sizePt * 1.2}pt; font-weight: 700; margin-bottom: 1em; }
.title-page .book-subtitle { font-size: ${s.typography.bodySizePt * 1.3}pt; font-style: italic; color: ${s.colors.muted}; margin-bottom: 4em; }
.title-page .book-author { font-size: ${s.typography.bodySizePt * 1.1}pt; }

/* ---------- Impressum / Copyright-Seite ------------------------------- */
/* Eigene benannte Seite → der automatische Umbruch der benannten Seite trennt
   sie von der Titelseite davor und vom Inhaltsverzeichnis danach. KEIN
   erzwungener recto/verso-Umbruch → fließt lückenlos in die Front-Matter
   (z. B. Cover S.1, Titel S.2, Impressum S.3, Inhaltsverzeichnis S.4).
   Inhalt sitzt unten bündig im Satzspiegel: Paged.js legt den Flow-Inhalt in
   einen Wrapper-div (".pagedjs_page_content > div", height: inherit = volle
   Satzspiegelhöhe). Diesen div zur Flex-Spalte mit justify-content:flex-end
   machen → der Impressum-Block wird nach unten geschoben. */
.pagedjs_page.pagedjs_impressum_page .pagedjs_page_content > div {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
}
.impressum-page {
  page: impressum;
  font-size: ${(s.typography.bodySizePt * 0.82).toFixed(1)}pt;
  line-height: 1.45;
  color: ${resolveColor("@text", s)};
  text-align: ${s.impressum.textAlign === "center" ? "center" : "left"};
}
/* text-align direkt auf die <p> — die globale Regel "p { text-align }" trifft
   sie sonst direkt und schlägt den geerbten Wert von .impressum-page. */
.impressum-page p { text-indent: 0; margin: 0 0 0.7em; text-align: ${s.impressum.textAlign === "center" ? "center" : "left"}; }
.impressum-page p:last-child { margin-bottom: 0; }
.impressum-logo {
  display: block;
  width: ${s.impressum.logoWidthMm}mm;
  max-width: 100%;
  height: auto;
  /* zentriert → margin-left/right:auto; linksbündig → 0 */
  margin: 0 ${s.impressum.logoAlign === "center" ? "auto" : "0"} 1.2em;
}
@page impressum {
  @top-left { content: none; } @top-right { content: none; }
  @bottom-center { content: none; } @bottom-left { content: none; } @bottom-right { content: none; }
}

/* ---------- Blockquote / Pull-Quote / Callout ------------------------- */

blockquote {
  ${s.blockquote.italic ? "font-style: italic;" : ""}
  margin: 1em ${s.blockquote.indentEm}em;
  ${s.blockquote.borderLeft ? `border-left: 2px solid ${s.colors.muted}; padding-left: 0.8em;` : ""}
  break-inside: avoid;
}
blockquote p { text-indent: 0; }

.pull-quote {
  font-size: ${(s.typography.bodySizePt * 1.4).toFixed(1)}pt;
  text-align: center;
  margin: 1.5em 2em;
  padding: 0.6em 0;
  border-top: 0.5pt solid ${s.colors.muted};
  border-bottom: 0.5pt solid ${s.colors.muted};
  font-style: italic;
  break-inside: avoid;
}

.epigraph {
  font-style: italic;
  text-align: right;
  margin: 1em 4em 3em 4em;
  font-size: ${(s.typography.bodySizePt * 0.95).toFixed(1)}pt;
  color: ${s.colors.muted};
}

/* ---------- Bilder ---------------------------------------------------- */

img {
  /* Standardbreite für Bilder OHNE eigene Breitenangabe. Pro-Bild-Breiten
     kommen als Inline-Style (max-width: min(NN%, 100%)) direkt am img und
     überschreiben diese Regel — deshalb hier KEIN !important. min(…, 100%)
     deckelt jede Breite auf die Seitenbreite, auch ein hartes width=480px. */
  max-width: min(${s.images.defaultWidthPct}%, 100%);
  height: auto;
  display: block;
  margin: 1em auto;
  /* KEIN break-inside: avoid — sonst kickt Paged.js Bilder die nicht passen */
}
figure { break-inside: avoid; margin: 1.5em 0; text-align: center; }
figcaption {
  font-size: ${(s.typography.bodySizePt * 0.85).toFixed(1)}pt;
  ${s.images.captionItalic ? "font-style: italic;" : ""}
  text-align: ${s.images.captionAlign};
  color: ${s.colors.muted};
  margin-top: 0.4em;
}

/* ---------- Listen ---------------------------------------------------- */

ul, ol { margin: 0.6em 0; padding-left: 1.4em; }
ul li, ol li { margin: 0.15em 0; text-indent: 0; }

/* ---------- Horizontal Rule = Scene-Break ----------------------------- */

hr {
  border: 0;
  margin: 1.5em 0;
  text-align: center;
}
hr::after {
  content: "* * *";
  color: ${s.colors.muted};
  letter-spacing: 0.8em;
  font-size: ${s.typography.bodySizePt}pt;
}

/* ---------- Drop-Cap ------------------------------------------------- */

${s.dropCap.enabled
        ? `
/* Drop-Cap auf den ersten <p> nach jedem <h1> (per JS-Markierung in
   wrapChapters gesetzt — robust auch wenn dazwischen H2/H3 stehen). */
p.dropcap-target::first-letter {
  float: left;
  font-family: ${cssFont(s.dropCap.fontFamily)};
  color: ${s.dropCap.color};
  font-weight: 700;
  font-size: ${(s.dropCap.lines * s.typography.bodyLineHeight * 0.95).toFixed(2)}em;
  line-height: 0.86;
  padding-top: 0.06em;
  padding-right: 0.08em;
  margin-bottom: -0.1em;
}
p.dropcap-target {
  text-indent: 0 !important;
}
`
        : ""}

/* ---------- Inhaltsverzeichnis ---------------------------------------- */

/* KEIN erzwungener recto/verso-Umbruch → das Inhaltsverzeichnis fließt direkt
   hinter das Impressum (eigene benannte Seite macht den Umbruch). KEIN
   break-after auf toc — der nachfolgende H1 hat break-before:right/page, das
   macht den Break. Sonst entsteht eine Leerseite zwischen ToC und Kapitel 1. */
.toc { page: toc; }
@page toc {
  ${s.runningHeader.hideOnChapterOpener ? "@top-left { content: none; } @top-right { content: none; }" : ""}
}

.toc .toc-title {
  font-family: ${cssFont(s.headings.h1.fontFamily)};
  font-size: ${s.headings.h1.sizePt * 0.8}pt;
  font-weight: 700;
  text-align: center;
  margin-bottom: 2em;
}

.toc-list { list-style: none; padding: 0; margin: 0; }

/* Mehrzeilige Einträge: Seitenzahl muss an die LETZTE Zeile, nicht an die
   ersten. Lösung: CSS-Grid auf <li> mit 2 Spalten (Text 1fr, Seitenzahl auto)
   und align-items: end — damit klebt die Seitenzahl an der unteren Kante des
   Text-Bereichs, also auf Höhe der letzten Zeile. <a> wird per display:contents
   transparent gemacht damit seine Kinder direkte Grid-Items werden (Click-Target
   bleibt, Accessibility OK).
   Die Punkt-Linie ist ein inline-block::after auf .toc-text mit
   width:100% + margin-right:-100% — klassischer CSS-Leader-Trick: streckt sich
   in den horizontalen Restraum der letzten Zeile, ohne die Box zu vergrößern. */
.toc-list li {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  column-gap: 0.4em;
  align-items: end;
  margin: 0.35em 0;
  line-height: 1.4;
  break-inside: avoid;
}
.toc-list a {
  display: contents;
  text-decoration: none;
  color: inherit;
}
.toc-text {
  display: block;
  min-width: 0;
  /* Clippt die ::after-Leader-Linie an der rechten Spalten-Kante. Ohne
     dies zeichnet width:100% + margin-right:-100% die Linie über die
     Grid-Spalte hinaus, sodass Punkte HINTER der Seitenzahl auftauchen. */
  overflow: hidden;
}
/* Leader (Punkte/Linie) als Pseudo-Element nach dem Text. width:100% +
   margin-right:-100% lässt es in den horizontalen Rest der letzten Textzeile
   fließen, ohne eine eigene Zeile zu erzwingen. */
${s.toc.leader === "dots"
        ? `.toc-text::after {
  content: "";
  display: inline-block;
  width: 100%;
  margin-right: -100%;
  margin-left: 0.4em;
  vertical-align: 0.25em;
  border-bottom: 1pt dotted ${s.colors.muted};
  height: 0.55em;
}`
        : s.toc.leader === "line"
            ? `.toc-text::after {
  content: "";
  display: inline-block;
  width: 100%;
  margin-right: -100%;
  margin-left: 0.4em;
  vertical-align: 0.25em;
  border-bottom: 0.5pt solid ${s.colors.muted};
  height: 0.55em;
}`
            : ``}
/* Altes Markup-Element bleibt erhalten, ist aber jetzt unsichtbar. */
.toc-leader {
  display: none;
}
.toc-page {
  align-self: end;
  font-variant-numeric: tabular-nums;
  color: ${s.colors.muted};
  min-width: 2em;
  text-align: right;
  /* Weißer Hintergrund deckt überlappende Dots ab, falls die Leader-Linie
     unter der Zahl durchscheint. */
  background: #fff;
  padding-left: 0.3em;
}

.toc-l1 > a { font-weight: 600; }
.toc-l1 { margin-top: 0.8em; }
.toc-l2 { padding-left: 1em; }
.toc-l3 { padding-left: 2em; font-size: 0.92em; color: ${s.colors.muted}; }
.toc-l4 { padding-left: 3em; font-size: 0.88em; color: ${s.colors.muted}; }

/* ---------- Allgemeine Buchsatz-Regeln -------------------------------- */

.no-break, .keep-together { break-inside: avoid; }
.break-before { break-before: page; }

/* Bilder als Figure mit Caption */
figure img { margin: 0 auto; }

/* ---------- Manuelle Umbrüche (Marker-Direktiven) --------------------- */

/* Seitenumbruch — die Box selbst nimmt keinen Platz; die Pille darunter wird
   im Preview-Modus per body.md2bp-show-markers sichtbar.
   Position:relative ist wichtig: die Pille positioniert sich absolut zum
   Marker, damit der Layout-Fluss nicht gestört wird. */
.md2bp-pagebreak {
  break-after: page;
  height: 0;
  margin: 0;
  padding: 0;
  border: 0;
  position: relative;
}
.md2bp-pagebreak > .md2bp-marker-pill {
  position: absolute;
  left: 0;
  top: -2px;
}
${s.page.spreadLayout
        ? `
.md2bp-pagebreak[data-type="recto"] { break-after: right; }
.md2bp-pagebreak[data-type="verso"] { break-after: left; }
`
        : `
/* spreadLayout aus: recto/verso degradieren auf normalen page-Break,
   sonst entstehen unerwünschte Vakat-Verso-Seiten. */
.md2bp-pagebreak[data-type="recto"],
.md2bp-pagebreak[data-type="verso"] { break-after: page; }
`}
/* Blank: zusätzliche Leerseite einschieben. Realisiert über ein After-
   Pseudoelement das eine eigene leere Seite besetzt. */
.md2bp-pagebreak[data-type="blank"]::after {
  content: "";
  display: block;
  break-after: page;
}

/* Block zusammenhalten: weder innerhalb noch direkt nach dem Block brechen. */
.md2bp-keep {
  break-inside: avoid;
}

/* Bild/Block auf eigene Seite */
.md2bp-fullpage {
  break-before: page;
  break-after: page;
  width: 100%;
  text-align: center;
}
.md2bp-fullpage img {
  max-width: 100%;
  max-height: ${H - s.page.marginTopMm - s.page.marginBottomMm}mm;
  height: auto;
  margin: 0 auto;
}

/* Inline: nicht umbrechen */
.md2bp-nobreak {
  white-space: nowrap;
}

/* Container-Marker: position:relative damit die Pille innen oben links sitzt */
.md2bp-keep, .md2bp-fullpage {
  position: relative;
}
.md2bp-keep > .md2bp-marker-pill,
.md2bp-fullpage > .md2bp-marker-pill {
  position: absolute;
  left: 0;
  top: -2px;
}

/* ---------- Marker-Pillen (Preview-Sichtbarmachung) ---------- */

/* Per Default unsichtbar (sowohl Preview als auch Druck). */
.md2bp-marker-pill {
  display: none;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 8pt;
  line-height: 1;
  padding: 3px 8px 3px 18px;
  border-radius: 999px;
  background: #fef3c7;
  color: #b45309;
  border: 1px solid #fcd34d;
  white-space: nowrap;
  cursor: pointer;
  user-select: none;
  font-style: normal;
  font-weight: 600;
  letter-spacing: 0;
  text-transform: none;
  text-align: left;
  text-indent: 0;
  position: relative;
  z-index: 100;
  box-shadow: 0 1px 3px rgba(0,0,0,0.12);
  vertical-align: middle;
  visibility: visible;  /* override Marker-parent visibility:hidden */
}
.md2bp-marker-pill::before {
  /* Icon-Punkt links der Pille */
  content: "";
  display: inline-block;
  position: absolute;
  left: 7px;
  top: 50%;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
  transform: translateY(-50%);
}
.md2bp-marker-pill-keep {
  background: #f3e8ff;
  color: #6d28d9;
  border-color: #ddd6fe;
}
.md2bp-marker-pill-fullpage {
  background: #e0f2fe;
  color: #0369a1;
  border-color: #bae6fd;
}
.md2bp-marker-pill:hover {
  filter: brightness(0.96);
  transform: translateY(-1px);
}

/* Sichtbar machen nur wenn der Body-Toggle aktiv ist. */
body.md2bp-show-markers .md2bp-marker-pill {
  display: inline-block;
}

/* Druck: Pillen IMMER aus, egal ob Toggle an oder nicht. */
@media print {
  .md2bp-marker-pill,
  body.md2bp-show-markers .md2bp-marker-pill { display: none !important; }
}
`;
}
// CSS-Font-Familie-Wert sicher quoten (Multiword-Familien brauchen Quotes).
// Der eingebettete Symbol-Fallback (BookFallback) steht IMMER vor dem
// generischen serif/sans-serif, damit fehlende Glyphen (z. B. „→") in einer
// eingebetteten Schrift landen statt in einem System-Font.
function cssFont(family) {
    const generic = /sans/i.test(family) ? "sans-serif" : "serif";
    if (!family)
        return `"${FALLBACK_FAMILY}", ${generic}`;
    const quoted = /^[A-Za-z0-9_-]+$/.test(family) ? family : `"${family}"`;
    return `${quoted}, "${FALLBACK_FAMILY}", ${generic}`;
}
